// The Claude-Code/Codex-inspired composer for the persistent-agent chat.
// Sticky bottom bar: auto-growing textarea (Enter sends, Shift+Enter newline),
// a status-aware send button (Steer ⚡ while the agent is WORKING), a delivery/
// kind popover behind a chevron, and slash commands — typing "/" as the first
// character opens a keyboard-navigable menu (/steer /now /queue /idle /inbox
// plus action commands /plan /dream /operate). Submission logic stays in the route;
// this owns composition.
import type { PersistentAgentEventDelivery, PersistentAgentStatus } from '@cortex/types';
import type { CSSProperties } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { HoldToRecordButton } from '../../common/HoldToRecordButton';

import './pa-chat-host.css';

type EventKind = 'instruction' | 'notification' | 'completion';
export type ChatActionCommand = 'plan' | 'dream' | 'operate';

const kindHints: Record<string, string> = {
  instruction: 'a directive the thread should act on',
  notification: 'something happened it should know about',
  completion: 'a task it was waiting on has finished',
};

const deliveryHints: Record<PersistentAgentEventDelivery, string> = {
  now: 'delivered right away (sendMessage) — wakes an idle thread or joins a run in progress',
  interrupt: 'interrupt — aborts the active run and acts immediately (steering)',
  'next-turn': 'queued (queueMessage) — handled after the current run finishes (runs now if idle)',
  'when-idle': 'only when idle — waits if a run is active, never interrupts',
  inbox: 'filed for later — recorded but never wakes the thread',
};

const deliveryOptions: PersistentAgentEventDelivery[] = [
  'interrupt',
  'now',
  'next-turn',
  'when-idle',
  'inbox',
];

type ISlashCommand = {
  command: string;
  hint: string;
} & ({ delivery: PersistentAgentEventDelivery } | { action: ChatActionCommand });

const slashCommands: ISlashCommand[] = [
  { command: '/steer', delivery: 'interrupt', hint: deliveryHints.interrupt },
  { command: '/now', delivery: 'now', hint: deliveryHints.now },
  { command: '/queue', delivery: 'next-turn', hint: deliveryHints['next-turn'] },
  { command: '/idle', delivery: 'when-idle', hint: deliveryHints['when-idle'] },
  { command: '/inbox', delivery: 'inbox', hint: deliveryHints.inbox },
  {
    command: '/plan',
    action: 'plan',
    hint: "draft a mission plan grounded in the agent's facts & tools — review, then approve or deny",
  },
  {
    command: '/dream',
    action: 'dream',
    hint: "consolidate this thread's context into the agent's filesystem memory",
  },
  {
    command: '/operate',
    action: 'operate',
    hint: 'start a durable team of continuous role workers driven by live score feedback',
  },
];

// Chip tint matches the per-message delivery badges in UserMessage.
const deliveryChipClass: Record<PersistentAgentEventDelivery, string> = {
  interrupt: 'pa-chat-chip--steer',
  now: 'pa-chat-chip--now',
  'next-turn': '',
  'when-idle': '',
  inbox: 'pa-chat-chip--muted',
};

// Button label reflects the chosen delivery so the user knows what will
// happen (Steer vs Queue vs Send) instead of a generic WORKING-only label.
const sendLabel: Record<PersistentAgentEventDelivery, string> = {
  interrupt: 'Steer ⚡',
  now: 'Send',
  'next-turn': 'Queue',
  'when-idle': 'Send',
  inbox: 'File',
};

// A glyph per delivery so the mode button + menu rows read at a glance.
// All monochrome dingbats — no full-color emoji — to match ⚡➤⏳⚙ elsewhere.
const deliveryIcon: Record<PersistentAgentEventDelivery, string> = {
  interrupt: '⚡',
  now: '➤',
  'next-turn': '⏳',
  'when-idle': '☾',
  inbox: '↧',
};

const defaultDelivery: PersistentAgentEventDelivery = 'now';
const defaultKind: EventKind = 'instruction';

export function ChatComposer({
  status,
  onSend,
  onCommand,
  disabled,
  voice,
}: {
  status: PersistentAgentStatus | null;
  onSend: (content: string, kind: EventKind, delivery: PersistentAgentEventDelivery) => void;
  /** Action slash commands — never POSTed as ordinary messages. */
  onCommand?: (command: ChatActionCommand, args: string) => void;
  disabled: boolean;
  /**
   * Bot page only. When the box is empty the send button becomes the voice
   * control and starts the realtime call.
   */
  voice?: {
    live: boolean;
    starting: boolean;
    level: number;
    error: string | null;
    toggle: () => void;
  } | null;
}) {
  const [value, setValue] = useState('');
  const [kind, setKind] = useState<EventKind>(defaultKind);
  const [delivery, setDelivery] = useState<PersistentAgentEventDelivery>(defaultDelivery);
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Slash menu: open while the composer content is exactly a "/prefix" token.
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const modeBtnRef = useRef<HTMLButtonElement>(null);
  const [slashPos, setSlashPos] = useState<{ left: number; bottom: number; width: number } | null>(
    null,
  );
  const [optionsPos, setOptionsPos] = useState<{
    right: number;
    bottom: number;
    width: number;
  } | null>(null);

  const terminal = status === 'STOPPED' || status === 'FAILED';
  const locked = disabled || terminal;

  // Auto-resize 1 → ~8 rows (CSS max-height caps it; JS tracks content).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 176)}px`;
  }, [value]);

  const slashActive = !slashDismissed && /^\/[a-z-]*$/.test(value) && value.length >= 1 && !locked;
  const slashMatches = slashActive ? slashCommands.filter((c) => c.command.startsWith(value)) : [];
  const slashOpen = slashActive && slashMatches.length > 0;

  // Position portaled menus above the composer / mode button (viewport coords).
  useLayoutEffect(() => {
    if (!slashOpen && !optionsOpen) {
      setSlashPos(null);
      setOptionsPos(null);
      return;
    }
    const place = () => {
      const composer = composerRef.current;
      if (!composer) return;
      const cr = composer.getBoundingClientRect();
      const gap = 8;
      if (slashOpen) {
        setSlashPos({
          left: Math.max(8, cr.left),
          bottom: Math.max(8, window.innerHeight - cr.top + gap),
          width: Math.min(360, Math.max(280, cr.width - 16)),
        });
      } else {
        setSlashPos(null);
      }
      if (optionsOpen) {
        const br = modeBtnRef.current?.getBoundingClientRect() ?? cr;
        setOptionsPos({
          right: Math.max(8, window.innerWidth - br.right),
          bottom: Math.max(8, window.innerHeight - br.top + gap),
          width: Math.min(360, Math.max(280, cr.width * 0.55)),
        });
      } else {
        setOptionsPos(null);
      }
    };
    place();
    window.addEventListener('resize', place);
    // Capture scroll from the messages scroller and any parent.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [slashOpen, optionsOpen, value]);

  const pickSlash = (cmd: ISlashCommand) => {
    if ('action' in cmd) {
      // Trailing space keeps the composer focused and closes the menu
      // (slashActive requires /^\/[a-z-]*$/ — space ends the match).
      setValue(`${cmd.command} `);
    } else {
      setDelivery(cmd.delivery);
      setValue('');
    }
    setSlashIndex(0);
    taRef.current?.focus();
  };

  const send = () => {
    const trimmed = value.trim();
    if (!trimmed || locked) return;
    const m = trimmed.match(/^\/(plan|dream|operate)(?:\s+([\s\S]*))?$/);
    if (m) {
      // Never POST action commands as chat messages — that makes the agent
      // "just run". Parent must wire onCommand (thread page + graph sidebar).
      if (onCommand) {
        onCommand(m[1] as ChatActionCommand, (m[2] ?? '').trim());
        setValue('');
      }
      return;
    }
    onSend(trimmed, kind, delivery);
    setValue('');
  };

  const nonDefault = delivery !== defaultDelivery || kind !== defaultKind;

  const slashMenu =
    slashOpen && slashPos
      ? createPortal(
          <div
            className="pa-chat-popover pa-chat-popover--portal"
            role="menu"
            style={{
              position: 'fixed',
              left: slashPos.left,
              bottom: slashPos.bottom,
              width: slashPos.width,
              right: 'auto',
            }}
          >
            {slashMatches.map((cmd, i) => (
              <button
                key={cmd.command}
                type="button"
                className={`pa-chat-option${i === slashIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setSlashIndex(i)}
                onClick={() => pickSlash(cmd)}
              >
                {'delivery' in cmd ? (
                  <>
                    <span className="pa-chat-option-name">
                      <span className="pa-chat-option-icon">{deliveryIcon[cmd.delivery]}</span>
                      {cmd.command} → {cmd.delivery}
                    </span>
                    <span className="pa-chat-option-hint">{cmd.hint}</span>
                  </>
                ) : (
                  <>
                    <span className="pa-chat-option-name">
                      <span className="pa-chat-option-icon">
                        {cmd.action === 'plan' ? '⚑' : cmd.action === 'dream' ? '✦' : '⚙'}
                      </span>
                      {cmd.command}
                    </span>
                    <span className="pa-chat-option-hint">{cmd.hint}</span>
                  </>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  const optionsMenu =
    optionsOpen && optionsPos
      ? createPortal(
          <div
            className="pa-chat-popover pa-chat-popover--portal"
            style={{
              position: 'fixed',
              right: optionsPos.right,
              bottom: optionsPos.bottom,
              width: optionsPos.width,
              left: 'auto',
            }}
          >
            <div className="field-label" style={{ marginBottom: 4 }}>
              Delivery — when it arrives
            </div>
            {deliveryOptions.map((d) => (
              <button
                key={d}
                type="button"
                className={`pa-chat-option${d === delivery ? ' is-active' : ''}`}
                onClick={() => {
                  setDelivery(d);
                  setOptionsOpen(false);
                  taRef.current?.focus();
                }}
              >
                <span className="pa-chat-option-name">
                  <span className="pa-chat-option-icon">{deliveryIcon[d]}</span>
                  {d}
                </span>
                <span className="pa-chat-option-hint">{deliveryHints[d]}</span>
              </button>
            ))}
            <label className="field" style={{ marginTop: 8 }}>
              <span className="field-label">Kind — what this is</span>
              <select
                className="select-native"
                value={kind}
                onChange={(e) => setKind(e.target.value as EventKind)}
              >
                <option value="instruction">Instruction (do this)</option>
                <option value="notification">Notification (heads-up)</option>
                <option value="completion">Completion (task finished)</option>
              </select>
            </label>
            <p className="t-xs t-muted" style={{ margin: '6px 2px 0' }}>
              Sending {kindHints[kind]}; {deliveryHints[delivery]}.
            </p>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="pa-chat-composer" ref={composerRef}>
      {slashMenu}
      {optionsMenu}
      <div className="pa-chat-composer-row">
        <div className={`pa-chat-input-well${locked ? ' is-locked' : ''}`}>
          <textarea
            ref={taRef}
            className="pa-chat-composer-textarea"
            placeholder="Message… (/ for commands)"
            value={value}
            rows={1}
            disabled={locked}
            onChange={(e) => {
              setValue(e.target.value);
              setSlashIndex(0);
              if (!e.target.value.startsWith('/')) setSlashDismissed(false);
            }}
            onKeyDown={(e) => {
              if (slashOpen) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSlashIndex((i) => (i + 1) % slashMatches.length);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  const choice = slashMatches[slashIndex] ?? slashMatches[0];
                  if (choice) pickSlash(choice);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setSlashDismissed(true);
                  return;
                }
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <HoldToRecordButton
            className="pa-chat-mic"
            disabled={locked}
            onText={(t) => {
              setValue((prev) => (prev ? `${prev} ${t}` : t));
              taRef.current?.focus();
            }}
          />
          {nonDefault && (
            <button
              type="button"
              className={`pa-chat-chip ${deliveryChipClass[delivery] ?? ''}`}
              title="Reset to defaults (instruction · now)"
              onClick={() => {
                setDelivery(defaultDelivery);
                setKind(defaultKind);
              }}
            >
              {kind !== defaultKind ? `${kind} · ` : ''}
              {delivery} ✕
            </button>
          )}
          <button
            ref={modeBtnRef}
            type="button"
            className="pa-chat-mode-btn"
            title={`Delivery: ${delivery} — ${deliveryHints[delivery]}. Click to change.`}
            aria-label={`Delivery mode: ${delivery}. Click to change.`}
            onClick={() => setOptionsOpen((v) => !v)}
            disabled={locked}
          >
            <span className="pa-chat-mode-icon">{deliveryIcon[delivery]}</span>
          </button>
        </div>
        <button
          type="button"
          className={`btn btn--primary pa-chat-send-btn${voice && !value.trim() ? ' is-voice' : ''}${voice?.live ? ' is-live' : ''}`}
          data-voice={
            voice && !value.trim()
              ? voice.starting
                ? 'connecting'
                : voice.live
                  ? 'listening'
                  : undefined
              : undefined
          }
          style={
            voice?.live ? ({ '--voice-level': voice.level.toFixed(3) } as CSSProperties) : undefined
          }
          aria-pressed={voice && !value.trim() ? voice.live : undefined}
          disabled={voice && !value.trim() ? locked : !value.trim() || locked}
          onClick={() => {
            if (voice && !value.trim()) voice.toggle();
            else send();
          }}
          title={
            voice && !value.trim()
              ? voice.live
                ? 'Stop voice'
                : 'Talk to this bot'
              : 'Send (Enter — Shift+Enter for newline)'
          }
          aria-label={
            voice && !value.trim() ? (voice.live ? 'Stop voice' : 'Talk to this bot') : 'Send'
          }
        >
          {voice && !value.trim() ? <VoiceWaveIcon /> : sendLabel[delivery]}
        </button>
      </div>
      {voice?.error ? <p className="pa-chat-composer-hint is-danger">{voice.error}</p> : null}
      {status === 'PAUSED' && (
        <p className="pa-chat-composer-hint">
          Agent is paused — message will be processed on resume
        </p>
      )}
      {status === 'WAITING_FOR_INPUT' && (
        <p className="pa-chat-composer-hint">
          Agent is waiting on a human answer — answer its question above or from the decisions bell
          in the top bar.
        </p>
      )}
      {terminal && (
        <p className="pa-chat-composer-hint is-danger">Agent is {status?.toLowerCase()}</p>
      )}
    </div>
  );
}

/** Five bars, the same mark Understudy uses for the voice button. */
function VoiceWaveIcon() {
  return (
    <svg
      className="voice-wave"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="2" y="9" width="3" height="6" rx="1.5" style={{ ['--vb' as string]: 1.4 }} />
      <rect x="6.5" y="6" width="3" height="12" rx="1.5" style={{ ['--vb' as string]: 2.2 }} />
      <rect x="11" y="3" width="3" height="18" rx="1.5" style={{ ['--vb' as string]: 3 }} />
      <rect x="15.5" y="6" width="3" height="12" rx="1.5" style={{ ['--vb' as string]: 2.2 }} />
      <rect x="20" y="9" width="3" height="6" rx="1.5" style={{ ['--vb' as string]: 1.4 }} />
    </svg>
  );
}
